import React, {useEffect, useState} from 'react';
import {array, func, number} from 'prop-types';
import classnames from 'classnames';
import InfoIcon from '@material-ui/icons/Info';
import {Link, useLocation, useHistory} from 'react-router-dom';
import {curry} from 'ramda';
import {useDebouncedCallback} from 'use-debounce';
import wrap from 'wraptile';
import ReplayIcon from '@material-ui/icons/Replay';
import humanizeString from 'humanize-string';
import useQuery from '@pluto/query-hook';
import {getInitialPage, getInitialRowsPerPage} from '@pluto/table';
import {formQuery} from '@pluto/rest';
import {baseRoutes} from 'App/AppRouter/routes';
import {setSearchParams} from 'QCWorkflow/QCManager/QCManagerContainer';
import {TableRowType} from 'QCWorkflow/DeliveryManager/DeliveryManagerTypes';
import IngestList from './IngestList';
import ingestListTableColumns from './IngestListStaticColumns';
import Tooltip from '@material-ui/core/Tooltip';
import {isFailedStatus} from '@pluto/utils';

export const renderIngestSessionLink = curry((location, {id, status}) => {
    return (
        <Link to={`/ingest-list/sessions/${id}?status=${status}`}>
            <InfoIcon
                style={{
                    color: '#fff',
                }}
            />
        </Link>
    );
});

export const renderRestartIcon = curry((
    queryParams: {[key: string]: string},
    restartTask: (id: number) => void,
    handleRestartClick: (id: number, params: {[key: string]: string}, restartTask: (id: number) => void)=> void,
    {status, id}: TableRowType
) => {
    return status.toLowerCase() === 'failed' ? (
        <Tooltip
            title="Restart ingest session"
        >
            <div className="icons-container">
                <span role="button" onClick={handleRestartClick(id, queryParams, restartTask) as any}>
                    <ReplayIcon/>
                </span>
            </div>
        </Tooltip>
    ) : null;
});

export const renderIngestStatusLink = curry((location, {id, status}) => {
    if (isFailedStatus(status))
        return (
            <Link
                to={`/ingest-list/error-session/${id}/${location}`}
                className="ingest-status-failed"
            >
                {humanizeString(status)}
            </Link>
        );

    return humanizeString(status);
});

export const renderValidationStatusLink = curry((location, {validation, validationStatus}) => {
    if (!validationStatus)
        return;

    const isPassed = validationStatus === 'PASSED';
    const isFailed = validationStatus === 'FAILED';

    return (
        <Link
            to={`/ingest-list/validation/${validation}/${location}`}
            className={classnames({
                'ingest-passed': isPassed,
                'ingest-status-failed': isFailed,
            })}
        >
            {humanizeString(validationStatus)}
        </Link>
    );
});

export const renderIngestType = curry((ingestTypesText: {[key: string]: string}, {feed: {ingestConfig}}: TableRowType) => {
    return ingestTypesText ? ingestTypesText[ingestConfig?.ingestType] : '';
});
export const renderLabPartner = ({feed: {ingestConfig}}: TableRowType) => ingestConfig?.labPartner;
export const getParamsObj = (queryString: string) => {
    const params: {[key: string]: string} = {};
    const queryStringArr = queryString.split('&');

    for (const item of queryStringArr) {
        const itemArr = item.split('=');
        params[itemArr[0]] = itemArr[1]
    }

    return params;
}

export const handleRestartClick = wrap((id: number, params: any, restartTask: (id: number, params: {[key: string]: string}) => void) => restartTask(id, params));

export default function IngestListContainer(props: any) {
    const {
        ingestList,
        total,
        setIngestList,
        restartTask,
        ingestTypesText,
    } = props;
    const query = useQuery();
    const history = useHistory();
    const {search} = useLocation();
    const queryParams = getParamsObj(query.toLocaleString());
    const [title, setTitle] = useState(query.get('title') || '');
    const [filters, setFilter] = useState({
        contentPartnerId: query.get('contentPartnerId') || '',
        type: query.get('type') || '',
        labPartner: query.get('labPartner') || '',
        ingestType: query.get('ingestType') || '',
        status: query.get('status') || '',
        validationStatus: query.get('validationStatus') || '',
    });
    const initialRowsPerPage = getInitialRowsPerPage(query);
    const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
    const initialPage = getInitialPage({
        query,
        rowsPerPage,
    });
    const [page, setPage] = useState(initialPage);
    const DEBOUNCE_TIME = 700;

    const cols = [
        ...ingestListTableColumns, {
            dataKey: 'ingestType',
            title: 'Ingest type',
            render: renderIngestType(ingestTypesText),
            width: 8,
        }, {
            dataKey: 'labPartner',
            title: 'Lab partner',
            render: renderLabPartner,
            width: 8,
        }, {
            dataKey: 'startTime',
            title: 'Started',
            width: 8,
        },
        {
            dataKey: 'endTime',
            title: 'Finished',
            width: 8,
        }, {
            dataKey: 'status',
            title: 'Ingest status',
            render: renderIngestStatusLink(search),
            width: 8,
        }, {
            dataKey: 'validation',
            title: 'Validation status',
            render: renderValidationStatusLink(search),
            width: 8,
        }, {
            dataKey: '',
            title: '',
            render: renderIngestSessionLink(search),
            width: 1,
        }, {
            dataKey: 'restart',
            title: ' ',
            render: renderRestartIcon(queryParams, restartTask, handleRestartClick),
            width: 1,
        },
    ];

    const params = {
        ...filters,
        title,
        offset: rowsPerPage * page,
        limit: rowsPerPage,
    };

    const filterHandlers = {
        setFilter,
        setPage,
        setQuery,
        history,
        setIngestList,
        filters,
        rowsPerPage,
        title,
    };

    const {
        handlePartnersChange,
        handleTypeChange,
        handleLabPartnerChange,
        handleStatusChange,
        handleValidationStatusChange,
        handleIngestTypeChange,
    } = getIngestListFilters(filterHandlers);

    const debounceCallback = handleDebounceCallback({
        ...filters,
        title,
        limit: rowsPerPage,
        offset: 0,
        setIngestList,
        setPage,
        setQuery,
        history,
    });

    const [debouncedFunction] = useDebouncedCallback(debounceCallback, DEBOUNCE_TIME);

    const onDebouncedSearch = handleDebounce({
        debouncedFunction,
        setTitle,
    });

    const onPaging = handleIngestListPaging({
        ...params,
        setIngestList,
        setQuery,
        history,
    });

    const onMount = handleMount({
        setIngestList,
        setRowsPerPage,
        setPage,
        params,
        query,
    });

    useEffect(onMount, []);
    useEffect(setSearchParams(query), [query]);

    return (
        <IngestList
            onDebouncedSearch={onDebouncedSearch}
            handlePartnersChange={handlePartnersChange}
            handleTypeChange={handleTypeChange}
            handleLabPartnerChange={handleLabPartnerChange}
            handleStatusChange={handleStatusChange}
            handleIngestTypeChange={handleIngestTypeChange}
            handleValidationStatusChange={handleValidationStatusChange}
            title={title}
            filters={filters}
            ingestList={ingestList}
            total={total}
            onPaging={onPaging}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            cols={cols}
        />
    );
}

IngestListContainer.propTypes = {
    ingestList: array.isRequired,
    setIngestList: func.isRequired,
    total: number.isRequired,
};

export const setQuery = (history: any, params: any) => {
    const search = formQuery('', params);

    history.push({
        pathname: baseRoutes.ingest,
        search,
    });
};

export const getIngestListFilters = (filterHandlers: any) => {
    const handlePartnersChange = handleFilters({
        ...filterHandlers,
        key: 'contentPartnerId',
    });

    const handleTypeChange = handleFilters({
        ...filterHandlers,
        key: 'type',
    });
    const handleLabPartnerChange = handleFilters({
        ...filterHandlers,
        key: 'labPartner',
    });

    const handleIngestTypeChange = handleFilters({
        ...filterHandlers,
        key: 'ingestType',
    });

    const handleStatusChange = handleFilters({
        ...filterHandlers,
        key: 'status',
    });

    const handleValidationStatusChange = handleFilters({
        ...filterHandlers,
        key: 'validationStatus',
    });

    return {
        handlePartnersChange,
        handleTypeChange,
        handleStatusChange,
        handleLabPartnerChange,
        handleValidationStatusChange,
        handleIngestTypeChange,
    };
};

export const handleIngestListPaging = curry((containerArgs, tablePaging) => {
    const {
        setIngestList,
        setQuery,
        history,
        ...containerParams
    } = containerArgs;
    const params = {
        ...containerParams,
        ...tablePaging,
    };

    setQuery(history, params);
    setIngestList(params);
});

export const handleDebounce = curry(({debouncedFunction, setTitle}, {target}) => {
    const {value} = target;

    setTitle(value);
    debouncedFunction(value);
});

export const handleDebounceCallback = curry((handlers, title) => {
    const {
        setIngestList,
        setPage,
        setQuery,
        history,
        ...params
    } = handlers;
    const newQuery = {
        ...params,
        title,
    };

    setPage(0);
    setQuery(history, newQuery);
    setIngestList(newQuery);
});

export const handleFilters = curry((presets, {target}) => {
    const {
        setFilter,
        setPage,
        setQuery,
        history,
        setIngestList,
        rowsPerPage,
        filters,
        title,
        key,
    } = presets;

    const value = String(target.value);

    const newFilters = {
        ...filters,
        [key]: value,
    };
    const query = {
        ...newFilters,
        offset: 0,
        title,
        limit: rowsPerPage,
    };

    setPage(0);
    setQuery(history, query);
    setFilter(newFilters);
    setIngestList(query);
});

export const handleMount = wrap((props: any) => {
    const {
        setIngestList,
        setPage,
        setRowsPerPage,
        params,
        query,
    } = props;

    const queryLimit = query.get('limit');

    if (queryLimit) {
        const queryOffset = query.get('offset');
        const limit = Number(queryLimit);
        const page = Number(queryOffset) / limit || 0;

        setRowsPerPage(limit);
        setPage(page);
        setIngestList({
            ...params,
            limit: queryLimit,
        });
        return;
    }

    setIngestList({
        ...params,
        limit: params.limit,
    });
});
